import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { formatAmount } from 'utils';
import { Grid, Container } from '@material-ui/core';
import Layout from 'components/Layout';
import Title from 'components/Title';
import Logs from 'components/Logs';
import Comments from 'components/Comments';
import { AccountStatementActions } from '../../state/modules/accountStatement';
import { useStyles } from './AccountStatementList.styled';
import { statusStyles } from './Common.styled';
import { FETCH_STATE } from 'constants';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { EntriesTable } from './EntriesTable';
import { ReservationsTable } from './ReservationsTable';
import { PaymentsTable } from './PaymentsTable';
import moment from 'moment';

const AccountStatementDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const statusClasses = statusStyles();
  // We use multiple selectors to avoid table rerender / consider using reselect.
  const statement = useSelector((state) => state?.accountStatement?.statement);
  const fetchingStatement = useSelector((state) => state?.accountStatement?.fetchingStatement);
  const fetchingAccountStatement = useSelector((state) => state?.accountStatement?.fetchingList);
  const localeFormat = useSelector((state) => state?.organizations?.localeFormat);
  const loading = fetchingAccountStatement === FETCH_STATE.LOADING;

  useEffect(() => {
    dispatch(AccountStatementActions.fetchAccountStatementById(id));
  }, [id]);

  const statementAvailable = fetchingStatement === FETCH_STATE.DONE;

  const invoiceDate = statementAvailable ? new Date(statement?.due_time) : null;
  const year =
    invoiceDate instanceof Date && !isNaN(invoiceDate)
      ? new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(invoiceDate)
      : '-';
  const month =
    invoiceDate instanceof Date && !isNaN(invoiceDate)
      ? new Intl.DateTimeFormat('en-US', { month: 'long' }).format(invoiceDate)
      : '-';
  const orgName = statement?.payer?.customer_name;
  const title = statementAvailable ? `${orgName}, ${month}, ${year}` : '--';

  return (
    <Layout title={i18n.t('account_statement.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={title} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.detailsContainer}>
              <div>
                <span className={classes.detailsItem}>{i18n.t('common.due_date')}: </span>
                <span>{moment(statement?.due_time).format(localeFormat)}</span>
              </div>
              <div>
                <span className={classes.detailsItem}>{i18n.t('common.id')}: </span>
                <span>{statement?.id || '--'}</span>
                <span>{` | `}</span>
                <span className={classes.detailsItem}>{i18n.t('common.total')}: </span>
                <span>{statement?.total ? `$${formatAmount(statement?.total)}` : '--'}</span>
                <span>{` | `}</span>
                <span className={classes.detailsItem} style={{ textTransform: 'uppercase' }}>
                  {i18n.t('common.status')}:{' '}
                </span>
                <span className={statusClasses[statement.status]}>
                  {statement.status
                    ? i18n.t(`account_statement.statuses.${statement.status}`, {
                        defaultValue: statement.status,
                      })
                    : '--'}
                </span>
                <span>{` | `}</span>
                <span className={classes.detailsItem}>{i18n.t('common.partnership')}: </span>
                <span className={statusClasses[statement?.relationship?.status]}>
                  {statement?.relationship?.status
                    ? i18n.t(`partners.statuses.${statement?.relationship?.status}`, {
                        defaultValue: statement?.relationship?.status,
                      })
                    : '--'}
                </span>
              </div>
            </div>
          </Grid>

          <EntriesTable accountStatementId={id} />

          <ReservationsTable accountStatementId={id} />

          <PaymentsTable accountStatementId={id} />
          <Grid item xs={4}>
            <Comments type="account_statement" id={+id} />
          </Grid>
          <Grid item xs={4}>
            <Logs
              targetId={parseInt(id)}
              targetType="AccountStatement"
              title={i18n.t('account_statement.logs')}
            />
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export { AccountStatementDetails };
